import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Cpu, Landmark, HeartPulse, ShieldCheck, Radio, ShoppingBag, CheckCircle2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import { INDUSTRIES, IMAGES } from '../mock/data';

const IconMap = { Cpu, Landmark, HeartPulse, ShieldCheck, Radio, ShoppingBag };

const SECTOR_HIGHLIGHTS = {
  Technology: ['SaaS & PaaS platforms', 'AI-native product companies', 'Deep-tech and infrastructure'],
  'Financial Services': ['Retail & investment banks', 'FinTech & payments', 'Capital markets & trading'],
  Healthcare: ['HealthTech platforms', 'Payers & providers', 'Life sciences & pharma'],
  Insurance: ['Carriers & MGAs', 'InsurTech & claims automation', 'Underwriting analytics'],
  Communications: ['Telecom & 5G', 'Media & streaming', 'Modern communication platforms'],
  'Retail & eCommerce': ['Omnichannel commerce', 'Marketplaces', 'DTC consumer brands'],
};

const Industries = () => (
  <>
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="hero-glow" />
      <div className="container-x relative">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Industries
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
            Deep expertise across <span className="shimmer-text">regulated & tech-forward</span> sectors.
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="text-white/60 text-lg max-w-2xl mt-6">
            We understand the languages, compliance realities and hiring rhythms of the industries we serve — so every placement is not just technically capable but truly contextual.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="grid md:grid-cols-2 gap-5">
          {INDUSTRIES.map((ind, i) => {
            const Ico = IconMap[ind.icon];
            const highlights = SECTOR_HIGHLIGHTS[ind.name] || [];
            return (
              <Reveal key={i} delay={i * 80}>
                <div className="tilt-card glass rounded-2xl p-8 h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300">
                      <Ico className="w-6 h-6" />
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-white">{ind.name}</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed">{ind.desc}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                    {highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/70">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 p-10 md:p-14 text-center">
          <div className="absolute inset-0">
            <img src={IMAGES.netA} alt="" className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#05070E]/90 to-[#05070E]/70" />
          </div>
          <div className="relative max-w-2xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">Don't see your industry?</h2>
            <p className="text-white/70 mt-4">We work across the modern tech landscape. Talk to us about your specific needs.</p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm mt-7">
              Talk to our team <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Industries;
